/**
 * The appliance drawing family — the ids `SwitchAnimation.vue` draws.
 *
 * The hub declares `settings.appliance_type` on its plug-like classes. The old
 * row also wrote the hub's Connected Load Name (`settings.device_name`), which is
 * what the hub's own appliance routes classify by — so picking an appliance still
 * seeds that name, and `auto` leaves it alone.
 */
import { switchKindOf } from '../../devices/deviceWidgetTypes';
import { readSetting, variantFromSetting, variantValues, type VariantFamily } from './types';

export const APPLIANCE_VARIANTS: VariantFamily = {
  drawing: 'appliance',
  settingKey: 'appliance_type',
  title: ['Appliance', 'Porabnik'],
  description: [
    'What this plug actually feeds. The tile draws it, and the connected load name follows.',
    'Kaj ta vtičnica napaja. Ploščica to nariše, ime porabnika pa se uskladi.',
  ],
  options: [
    { value: 'auto', icon: 'settings_suggest', label: ['Automatic — from the name', 'Samodejno — po imenu'] },
    { value: 'socket', icon: 'outlet', label: ['Socket', 'Vtičnica'] },
    { value: 'coffee', icon: 'coffee', label: ['Coffee machine', 'Kavni aparat'], load: 'Coffee Machine' },
    {
      value: 'washer',
      icon: 'local_laundry_service',
      label: ['Washing machine', 'Pralni stroj'],
      load: 'Washing Machine',
    },
    { value: 'dryer', icon: 'dry_cleaning', label: ['Dryer', 'Sušilni stroj'], load: 'Dryer' },
    { value: 'dishwasher', icon: 'countertops', label: ['Dishwasher', 'Pomivalni stroj'], load: 'Dishwasher' },
    { value: 'fridge', icon: 'kitchen', label: ['Fridge', 'Hladilnik'], load: 'Fridge' },
    { value: 'oven', icon: 'microwave', label: ['Oven', 'Pečica'], load: 'Oven' },
    { value: 'kettle', icon: 'blender', label: ['Kettle', 'Kuhalnik vode'], load: 'Kettle' },
    { value: 'dimmer', icon: 'lightbulb', label: ['Lamp', 'Svetilka'], load: 'Lamp' },
    { value: 'ev', icon: 'ev_station', label: ['EV charger', 'Polnilnica'], load: 'EV Charger' },
    { value: 'vacuum', icon: 'robot_vacuum', label: ['Robot vacuum', 'Sesalni robot'] },
    { value: 'mower', icon: 'grass', label: ['Robot mower', 'Kosilni robot'] },
    { value: 'siren', icon: 'notifications_active', label: ['Siren', 'Sirena'] },
    { value: 'bell', icon: 'doorbell', label: ['Doorbell', 'Zvonec'] },
    { value: 'pump', icon: 'water_pump', label: ['Pump', 'Črpalka'] },
    { value: 'valve', icon: 'valve', label: ['Valve', 'Ventil'] },
    { value: 'generic', icon: 'category', label: ['None — keep the glyph', 'Brez — obdrži ikono'] },
  ],
  defaultValue: 'auto',
  resolve: (device) =>
    variantFromSetting(device.settings, 'appliance_type', variantValues(APPLIANCE_VARIANTS)) ??
    switchKindOf({
      ...device,
      connectedLoadName:
        (readSetting(device.settings, 'device_name') as string | undefined) ?? device.connectedLoadName,
    }),
};
